import { getIdToken } from './authService';
import { authFetch } from '../utils/authFetch';
import type {
  SendMessageResponse,
  ListSessionsResponse,
  GetSessionMessagesResponse,
  ListBotsResponse,
  ApiError,
} from '../types/chat.types';

const API_ENDPOINT = process.env.REACT_APP_CHAT_API_ENDPOINT!;

/**
 * Get auth headers with JWT token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Handle API errors with proper error messages
 */
async function handleApiError(response: Response): Promise<never> {
  try {
    const error: ApiError = await response.json();
    const message = error.message || error.error || `HTTP ${response.status}`;
    throw new Error(message);
  } catch (e) {
    if (e instanceof Error) throw e;
    throw new Error(`HTTP ${response.status}`);
  }
}

/**
 * Send a message and get streaming endpoint details
 *
 * @param sessionId - Client-generated session ID (use crypto.randomUUID())
 * @param message - User's message content
 * @param botId - Bot ID to use for response
 * @returns Streaming endpoint URL and payload to POST
 */
export async function sendMessage(
  sessionId: string,
  message: string,
  botId: string
): Promise<SendMessageResponse> {
  const headers = await getAuthHeaders();

  const response = await authFetch(`${API_ENDPOINT}/chat/message`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      sessionId,
      message,
      botId,
    }),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
}

/**
 * List all user's chat sessions
 *
 * @param botId - Optional bot ID to filter sessions by
 * @returns Array of session metadata sorted by lastMessageAt (newest first)
 */
export async function listSessions(botId?: string): Promise<ListSessionsResponse> {
  const headers = await getAuthHeaders();

  const url = botId
    ? `${API_ENDPOINT}/chat/sessions?botId=${encodeURIComponent(botId)}`
    : `${API_ENDPOINT}/chat/sessions`;

  const response = await authFetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
}

/**
 * Get message history for a session
 *
 * @param sessionId - Session ID to retrieve
 * @returns Session metadata and message history
 */
export async function getSessionMessages(
  sessionId: string
): Promise<GetSessionMessagesResponse> {
  const headers = await getAuthHeaders();

  const response = await authFetch(
    `${API_ENDPOINT}/chat/sessions/${sessionId}/messages`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
}

/**
 * List bots user has access to
 *
 * @returns Array of bots the user can chat with
 */
export async function listBots(): Promise<ListBotsResponse> {
  const headers = await getAuthHeaders();

  const response = await authFetch(`${API_ENDPOINT}/chat/bots`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
}
